import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Box, Divider } from "@hawkins/components";
import { Dimensions } from "@hawkins/variables";
import { DropdownFormControl, InputFormControl } from "@hawkins/forms";

import { IdentifierPayload, MOPTypes, TranslationNamespace } from "../../types";
import { Repeater } from "./Repeater";
import { enumToDropdownOptions } from "../../utils";
import { CreditCardInputFormControl } from "./inputs/CreditCardInput";

type MOPRepeaterProps = {
  methods: any;
  disabled?: boolean;
};

function createMOPInputs(identifierPayloads: IdentifierPayload[], index: number, t: TFunction) {
  const field = identifierPayloads[index];
  const enumValue = MOPTypes[field?.mopType?.value as keyof typeof MOPTypes];
  switch (enumValue) {
    case MOPTypes.CC:
      return (
        <CreditCardInputFormControl
          key={`mop-${index}`}
          name={`memberIdPayload.identifierPayloads.${index}.mop`}
          label={t("member_id_option_2")}
          requirementType="required-star"
          data-testid={`mop-input-${index}`}
        />
      );
    case MOPTypes.GIFT_CARD:
    case MOPTypes.EU_DIRECT_DEBIT:
    case MOPTypes.PAYPAL:
      return (
        <InputFormControl
          key={`mop-${index}`}
          name={`memberIdPayload.identifierPayloads.${index}.mop`}
          label={t("member_id_option_2")}
          requirementType="required-star"
          fullWidth
          data-testid={`mop-input-${index}`}
        />
      );
    case MOPTypes.US_BA:
      return (
        <Box display="flex" width="100%" columnGap={Dimensions.Space8}>
          <InputFormControl
            key={`routingNumber-${index}`}
            name={`memberIdPayload.identifierPayloads.${index}.routingNumber`}
            label={t("mop_type7_field1")}
            requirementType="required-star"
            fullWidth
            data-testid={`us-routing-number-${index}`}
          />
          <InputFormControl
            key={`us-accountNumber-${index}`}
            name={`memberIdPayload.identifierPayloads.${index}.accountNumber`}
            label={t("mop_type7_field2")}
            requirementType="required-star"
            fullWidth
            data-testid={`us-account-number-${index}`}
          />
        </Box>
      );
    case MOPTypes.BR_BA:
      return (
        <Box display="grid" width="100%" columnGap={Dimensions.Space8} gridTemplateColumns={"40% 40% 20%"}>
          <InputFormControl
            key={`branchNumber-${index}`}
            name={`memberIdPayload.identifierPayloads.${index}.branchNumber`}
            label={t("mop_type8_field1")}
            requirementType="required-star"
            fullWidth
            data-testid={`br-branch-number-${index}`}
          />
          <InputFormControl
            key={`br-accountNumber-${index}`}
            name={`memberIdPayload.identifierPayloads.${index}.accountNumber`}
            label={t("mop_type7_field2")}
            requirementType="required-star"
            fullWidth
            data-testid={`br-account-number-${index}`}
          />
          <InputFormControl
            key={`digit-${index}`}
            name={`memberIdPayload.identifierPayloads.${index}.digit`}
            label={t("mop_type8_field2")}
            requirementType="required-star"
            fullWidth
            data-testid={`br-digit-${index}`}
          />
        </Box>
      );
    default:
      return null;
  }
};

export function MOPRepeater({ methods, disabled = false }: MOPRepeaterProps) {
  const { t } = useTranslation(TranslationNamespace.LEP);

  const cardOptions = enumToDropdownOptions(MOPTypes, t);

  const identifierPayloads = methods.watch('memberIdPayload.identifierPayloads');

  const onMopTypeChange = (idx: number) => {
    methods.resetField(`memberIdPayload.identifierPayloads.${idx}.mop`);
    methods.resetField(`memberIdPayload.identifierPayloads.${idx}.routingNumber`);
    methods.resetField(`memberIdPayload.identifierPayloads.${idx}.accountNumber`);
    methods.resetField(`memberIdPayload.identifierPayloads.${idx}.branchNumber`);
    methods.resetField(`memberIdPayload.identifierPayloads.${idx}.digit`);
  };

  return (
    <>
      <Repeater
        name="memberIdPayload.identifierPayloads"
        componentGenerator={(idx) => (
          <Box display="grid" gridTemplateColumns={"30% 70%"} paddingRight={Dimensions.Space8}>
            <Box display="flex" justifyContent="flex-start" width="100%" paddingRight={Dimensions.Space8}>
              <DropdownFormControl
                name={`memberIdPayload.identifierPayloads.${idx}.mopType`}
                label={t("mop_payment_type")}
                options={cardOptions}
                requirementType="required-star"
                onChange={() => onMopTypeChange(idx)}
                data-testid={`mop-type-dropdown-${idx}`}
              />
            </Box>
            <Box display="flex" justifyContent="flex-start" width="100%" paddingRight={Dimensions.Space8}>
              {createMOPInputs(identifierPayloads, idx, t)}
            </Box>
          </Box>
        )}
        defaultValueGenerator={() => ({})}
        removeText={t('button_remove')}
        addText={t('button_add')}
        methods={methods}
        disabled={disabled}
      />
      <Divider />
    </>
  );
}